// ============================================================================
// DEADLINE RUNWAY — LifeOS School.dc.html, option 6a, the 352px right column.
//
// One merged two-week list across all four courses, grouped by day, each row carrying a 3px left
// border in its course hue and a right-aligned time-remaining figure.
//
// THE TWO RULES THIS COMPONENT EXISTS TO KEEP (README §3):
//   1. OVERDUE IS PINNED AND NEVER SOFTENED. It is its own group, always first, on a danger
//      ground, with a danger-red left border rather than the course hue — the one place a course
//      loses its colour, because at that point the course is not the point.
//   2. TIME REMAINING AND ITS COLOUR ARE DERIVED, NEVER STORED (README §State Management):
//      overdue → danger-2, inside a day → urgent-time, otherwise → muted. `timeRemaining()` in
//      lib/time.ts owns the thresholds and truncates rather than rounds, so a deadline fourteen
//      hours and forty minutes gone is '−14h' and never '−15h'.
// ============================================================================

import type { CSSProperties } from 'react'
import { clockFromTimestamp, localDayOf, timeRemaining, type TimeTone } from '../../lib/time'
import { weekdayName, type Assignment, type DatedAssignment } from '@app/shared'

import { SkeletonRows } from '../Card'
import { CalendarPlain } from './icons'
import { AssignmentPeek, useAssignmentPeek } from './AssignmentPeek'
import { hueBorder, hueVar, runwayGroups, runwaySummary } from './derive'
import { Unavailable } from './Chrome'

const TIME_TONE: Record<TimeTone, string> = {
  overdue: 'text-danger-2 font-bold',
  urgent: 'text-urgent-time font-bold',
  calm: 'text-muted font-semibold',
}

export function DeadlineRunway({
  assignments,
  isPending,
  error,
  today,
  nowMinutes,
}: {
  assignments: Assignment[] | undefined
  isPending: boolean
  error: unknown
  today: string
  nowMinutes: number
}) {
  const rows = assignments ?? []
  const groups = runwayGroups(rows, today, nowMinutes)
  const summary = runwaySummary(rows, today, nowMinutes)
  const peek = useAssignmentPeek()

  return (
    <section className="flex w-[352px] min-h-0 flex-none flex-col gap-3 overflow-hidden rounded-context border border-line bg-surface px-5 py-[18px] shadow-card">
      <header className="flex flex-none items-center gap-[9px]">
        <CalendarPlain size={17} strokeWidth={1.7} className="flex-none text-tasks" />
        <h2 className="text-[13px] font-semibold leading-none">Deadline runway</h2>
        <span className="ml-auto text-[11px] font-medium leading-none text-muted">
          next 14 days · all courses
        </span>
      </header>

      {isPending && <SkeletonRows rows={6} />}
      <Unavailable error={error} />

      {assignments && rows.length === 0 && (
        <p className="text-[12px] leading-[1.45] text-microcopy">
          Nothing open in the next two weeks.
        </p>
      )}

      {assignments && rows.length > 0 && (
        <div className="flex flex-none items-center gap-2 rounded-tile bg-danger-fill px-3 py-[9px]">
          {summary.overdue > 0 ? (
            <>
              <span className="size-[6px] flex-none rounded-full bg-danger-dot" aria-hidden />
              <span className="text-[11.5px] font-semibold leading-none text-danger-2">
                {summary.overdue} overdue
              </span>
              <span className="text-[11.5px] font-normal leading-none text-danger-dot">
                · {summary.thisWeek} due this week · {summary.nextWeek} next week
              </span>
            </>
          ) : (
            // The strip keeps its shape when nothing is overdue but loses its alarm: it is a
            // summary line, and a permanently red one stops meaning anything.
            <span className="text-[11.5px] font-medium leading-none text-ink-2">
              Nothing overdue · {summary.thisWeek} due this week · {summary.nextWeek} next week
            </span>
          )}
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col gap-[11px] overflow-y-auto">
        {groups.map((group) => (
          <div key={group.key} className="flex flex-col gap-[11px]">
            <div className="flex items-center gap-[9px]">
              <span
                className={[
                  'whitespace-nowrap text-[10px] font-bold leading-none tracking-[0.07em]',
                  group.overdue ? 'text-danger-2' : 'text-ink-2',
                ].join(' ')}
              >
                {group.label}
              </span>
              <span className="h-px flex-1 bg-fill" aria-hidden />
            </div>

            {group.items.map((a) => (
              <RunwayRow
                key={a.id}
                assignment={a}
                overdue={group.overdue}
                today={today}
                nowMinutes={nowMinutes}
                expanded={peek.open?.assignment.id === a.id}
                onOpen={(target) => peek.openFor(a, target)}
              />
            ))}
          </div>
        ))}
      </div>

      {peek.open && (
        <AssignmentPeek
          assignment={peek.open.assignment}
          // The overview has no single course in hand — the runway is every course at once — so
          // the DUE line's "in class" derivation is simply not available here. It falls back to
          // the clock time, which is what Canvas actually said.
          course={null}
          anchor={peek.open.anchor}
          onClose={peek.close}
        />
      )}
    </section>
  )
}

/**
 * ONE ROW, AND NOW A BUTTON RATHER THAN A LINK (2026-08-29).
 *
 * It used to navigate to the course page, which answered a question the owner was not asking: he
 * clicks a deadline to find out WHAT IT IS, and landing on a course page full of other work is a
 * detour to the same table he could already see. The peek answers it in place — description,
 * points, lock date, how it is handed in — and carries both doors out of itself: "Open in
 * bCourses" and "Open in Course". Nothing that was reachable stopped being reachable; it is one
 * click further, and the click before it now answers the question.
 */
function RunwayRow({
  assignment,
  overdue,
  today,
  nowMinutes,
  expanded,
  onOpen,
}: {
  // A runway row IS a countdown, so it can only be built from work that has a deadline.
  // `runwayGroups()` filters the undated out; the type is what stops one arriving another way.
  assignment: DatedAssignment
  overdue: boolean
  today: string
  nowMinutes: number
  expanded: boolean
  onOpen: (target: Element) => void
}) {
  const t = timeRemaining(assignment.dueAt, today, nowMinutes)
  const when = clockFromTimestamp(assignment.dueAt)

  return (
    <button
      type="button"
      onClick={(event) => onOpen(event.currentTarget)}
      aria-expanded={expanded}
      aria-haspopup="dialog"
      style={
        {
          '--mo-rule': overdue ? 'var(--color-danger-dot)' : hueVar(assignment.courseCode),
        } as CSSProperties
      }
      className={[
        'mo-rule flex w-full gap-[10px] rounded-tile border-l-[3px] px-[11px] py-[9px] text-left',
        overdue
          ? 'border-danger-dot bg-danger-fill'
          : `${hueBorder(assignment.courseCode)} bg-surface-2`,
      ].join(' ')}
    >
      <span className="min-w-0 flex-1">
        <span className="block text-[12.5px] font-semibold leading-[1.2] text-pretty">
          {assignment.title}
        </span>
        <span
          className={[
            'mt-1 block text-[11px] leading-none',
            overdue ? 'font-medium text-danger-dot' : 'font-normal text-muted',
          ].join(' ')}
        >
          {/* An overdue row names the DAY it was due as well as the time. It can be a fortnight
              gone, and "was 11:59pm" without a day is a deadline with no distance. */}
          {assignment.courseCode} ·{' '}
          {overdue ? `was ${weekdayName(localDayOf(assignment.dueAt), true)} ${when}` : when}
        </span>
      </span>
      <span
        className={['flex-none whitespace-nowrap text-[11px] leading-none', TIME_TONE[t.tone]].join(
          ' ',
        )}
      >
        {t.label}
      </span>
    </button>
  )
}
